import { useEffect, useRef } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  MissionChangedDTO, MissionDetailDTO, MissionListItemDTO, MissionPatchBody, MissionStateBody, RunLogPageDTO, RunsQuery,
} from '@contracts/assistant-missions';
import type {
  OrderCreateBody, OrderDTO, OrderPatchBody, OrderRanDTO,
} from '@contracts/assistant-orders';
import type { Id } from '@contracts/common';
import { api, type ApiClientError } from '../../../api/client';

/**
 * The missions API the web reads and writes (S7 §9.2). G9 needs only the cancel an undo refused as stale offers (G3);
 * the missions page (T17) adds the rest here.
 */

export const missionPath = (id: Id): string => `/api/assistant/missions/${encodeURIComponent(id)}`;

export const getMission = (id: Id): Promise<MissionDetailDTO> => api<MissionDetailDTO>(missionPath(id));

/** The reader's missions, in the server's order (waiting, active, paused, then the rest; §9.2). */
export const listMissions = (): Promise<MissionListItemDTO[]> => api<MissionListItemDTO[]>('/api/assistant/missions');

/** Every mission query: the list and each detail sit under it, so one invalidation reads the page again. */
export const MISSIONS_KEY = ['assistant', 'missions'] as const;
export const missionKey = (id: Id) => [...MISSIONS_KEY, id] as const;

export function useMissions(): UseQueryResult<MissionListItemDTO[], ApiClientError> {
  return useQuery<MissionListItemDTO[], ApiClientError>({ queryKey: MISSIONS_KEY, queryFn: listMissions });
}

/** How often a mission with a live run is read again (the tray's pace for a resuming run). */
export const MISSION_LIVE_POLL_MS = 3_000;

/** One of the mission's runs is still queued or running on the server. */
export const hasLiveRun = (m: MissionDetailDTO | undefined): boolean =>
  m?.runs.some((r) => r.status === 'queued' || r.status === 'running') ?? false;

/**
 * One mission. While one of its runs is live (an answer, "Chạy ngay", the start run), it is read again every few
 * seconds on a visible tab, so the steps, the question and the journal follow the run with no click; when the run has
 * ended, the list and the run log are read again too (their bars and rows changed with it). A mission with no live run
 * is read once.
 */
export function useMission(id: Id | null): UseQueryResult<MissionDetailDTO, ApiClientError> {
  const queryClient = useQueryClient();
  const query = useQuery<MissionDetailDTO, ApiClientError>({
    queryKey: missionKey(id ?? ''), queryFn: () => getMission(id!), enabled: id !== null,
    // C-F1: react-query skips a tick on a hidden tab (`refetchIntervalInBackground` false); the interval itself must not
    // read the tab, or a read that lands while it is hidden stops the poll for good (no refetch on focus here).
    refetchInterval: (q) => (hasLiveRun(q.state.data) ? MISSION_LIVE_POLL_MS : false),
    refetchIntervalInBackground: false,
  });
  const live = hasLiveRun(query.data);
  const wasLive = useRef(false);
  useEffect(() => {
    if (wasLive.current && !live) {
      void queryClient.invalidateQueries({ queryKey: MISSIONS_KEY, exact: true });
      void queryClient.invalidateQueries({ queryKey: [...MISSIONS_KEY, 'runs'] });
    }
    wasLive.current = live;
  }, [live, queryClient]);
  return query;
}

export const setMissionState = (id: Id, body: MissionStateBody, requestId: Id): Promise<MissionChangedDTO> =>
  api<MissionChangedDTO>(`${missionPath(id)}/state`, { method: 'POST', body, requestId });

/** "Hủy nhiệm vụ": the mission as it is now (its version), then the cancel on that version. */
export async function cancelMission(id: Id, requestId: Id): Promise<MissionChangedDTO> {
  const { version } = await getMission(id);
  return setMissionState(id, { state: 'cancel', version }, requestId);
}

/** "Gửi" under Hana's question: `{ text }`, 1–2 000 characters, only while the mission waits (§9.2). */
export const answerMission = (id: Id, text: string, requestId: Id): Promise<MissionChangedDTO> =>
  api<MissionChangedDTO>(`${missionPath(id)}/answer`, { method: 'POST', body: { text }, requestId });

/** "Chạy ngay": a `manual` run now (§6.4); a live run → 409 `RUN_ALIVE`. */
export const runMissionNow = (id: Id, requestId: Id): Promise<MissionChangedDTO> =>
  api<MissionChangedDTO>(`${missionPath(id)}/run`, { method: 'POST', body: {}, requestId });

/** The level and the limits ("Sửa trần"), on the version the page read. */
export const patchMission = (id: Id, body: MissionPatchBody, requestId: Id): Promise<MissionChangedDTO> =>
  api<MissionChangedDTO>(missionPath(id), { method: 'PATCH', body, requestId });

/** Only a finished mission (done, cancelled); its cards stay. */
export const deleteMission = (id: Id, requestId: Id): Promise<{ id: Id }> =>
  api<{ id: Id }>(missionPath(id), { method: 'DELETE', requestId });

/** `GET /api/assistant/runs`: the filters, then the cursor, each only when set. */
export function runsPath(q: RunsQuery): string {
  const params = new URLSearchParams();
  if (q.missionId !== undefined) params.set('missionId', q.missionId);
  if (q.orderId !== undefined) params.set('orderId', q.orderId);
  if (q.status !== undefined) params.set('status', q.status);
  if (q.cursor !== undefined) params.set('cursor', q.cursor);
  const query = params.toString();
  return query === '' ? '/api/assistant/runs' : `/api/assistant/runs?${query}`;
}

export type RunLogFilter = Omit<RunsQuery, 'cursor'>;

/**
 * "Nhật ký chạy", a cursor page at a time (§9.1). The filter is in the key, so a changed filter starts again from the
 * newest page with no cursor; "Xem thêm" fetches the next page on the last page's cursor. Under `MISSIONS_KEY`, so a
 * write on the page reads the log again too.
 */
export function useRunLog(filter: RunLogFilter) {
  return useInfiniteQuery<RunLogPageDTO, ApiClientError>({
    queryKey: [...MISSIONS_KEY, 'runs', filter],
    queryFn: ({ pageParam }) => api<RunLogPageDTO>(runsPath({ ...filter, cursor: pageParam as string | undefined })),
    initialPageParam: undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

// Standing orders (S7 §7.1, §9.2; L4 T13, T14). Owner only: the server lists the reader's own and answers 404 for
// anyone else's, so the page never filters on its own.

export const ORDERS_PATH = '/api/assistant/orders';
export const orderPath = (id: Id): string => `${ORDERS_PATH}/${encodeURIComponent(id)}`;
/** The orders list: under `MISSIONS_KEY`, so a write anywhere on the page reads it again with the rest. */
export const ORDERS_KEY = [...MISSIONS_KEY, 'orders'] as const;

/** One of the orders has a run queued or running ("Chạy thử ngay", an event, the schedule). */
export const hasLiveOrder = (orders: OrderDTO[] | undefined): boolean => orders?.some((o) => o.live) ?? false;

/** The reader's standing orders, read once (the form's "Tải lại" after a stale version). */
export const listOrders = (): Promise<OrderDTO[]> => api<OrderDTO[]>(ORDERS_PATH);

/**
 * The reader's standing orders, in the server's order. While one has a live run it is read again every few seconds on a
 * visible tab (the missions' pace), so "Lần gần nhất" follows the run with no click.
 */
export function useOrders(): UseQueryResult<OrderDTO[], ApiClientError> {
  return useQuery<OrderDTO[], ApiClientError>({
    queryKey: ORDERS_KEY, queryFn: listOrders,
    refetchInterval: (q) => (hasLiveOrder(q.state.data) ? MISSION_LIVE_POLL_MS : false),
    refetchIntervalInBackground: false,
  });
}

/** "+ Lệnh mới" (D202: the server admits it as `order.create` does). */
export const createOrder = (body: OrderCreateBody, requestId: Id): Promise<OrderDTO> =>
  api<OrderDTO>(ORDERS_PATH, { method: 'POST', body, requestId });

/** A change and/or on/off, on the version the page read (P17; 409 `VERSION_CONFLICT` otherwise). */
export const patchOrder = (id: Id, body: OrderPatchBody, requestId: Id): Promise<OrderDTO> =>
  api<OrderDTO>(orderPath(id), { method: 'PATCH', body, requestId });

/** P20: the order, its runs and their log go; what the runs did stays. */
export const deleteOrder = (id: Id, requestId: Id): Promise<{ id: Id }> =>
  api<{ id: Id }>(orderPath(id), { method: 'DELETE', requestId });

/** "Chạy thử ngay" (P18): a `manual` run; off → 409 `ORDER_OFF`, a live run → 409 `RUN_ALIVE`. */
export const runOrderNow = (id: Id, requestId: Id): Promise<OrderRanDTO> =>
  api<OrderRanDTO>(`${orderPath(id)}/run`, { method: 'POST', body: {}, requestId });
